<template>
  <div
    ref="carouselItem"
    class="app-carousel-item"
    :class="{ 'app-carousel-item-vertical': orientation === 'vertical' }"
    v-on="listeners"
  >
    <VNodes :vnodes="vnodes" />
    <div
      v-for="(element, index) in remainingElements"
      :key="index + element"
      class="app-carousel-item-stub"
    ></div>
  </div>
</template>

<script>
import { isEqual } from 'lodash-es';

export default {
  name: 'AppCarouselItem',
  components: {
    // Dummy component to render vnodes
    VNodes: {
      functional: true,
      render: (h, ctx) => ctx.props.vnodes,
    },
  },
  props: {
    vnodes: {
      type: Array,
      default: () => [],
    },
    itemsPerSlide: {
      type: Number,
      default: 3,
    },
    registerTouchListeners: {
      type: Boolean,
      default: false,
    },
    orientation: {
      type: String,
      default: 'horizontal',
    },
    clickHandler: {
      type: Function,
      default: () => {},
    },
    index: {
      type: Number,
      default: 0,
    },
  },
  data: () => ({
    initialTouchPos: {
      x: 0,
      y: 0,
    },
    lastTouchPos: {
      x: 0,
      y: 0,
    },
    rafPending: false,
    currentXPosition: 0,
    isScrolling: false,
  }),
  computed: {
    // Fill the rest of the slide space with empty elements
    remainingElements() {
      return this.itemsPerSlide - this.vnodes.length;
    },
    // Register listeners conditionally for the mobile screens
    listeners() {
      return this.registerTouchListeners
        ? {
            mousedown: this.handleGestureStart,
            touchstart: this.handleGestureStart,
            touchmove: this.handleGestureMove,
            touchend: this.handleGestureEnd,
            touchcancel: this.handleGestureEnd,
          }
        : null;
    },
  },
  methods: {
    swipe(initialPos, lastPos, e) {
      const triggerSwipeDistance = this.$el.clientWidth / 2;

      // Trigger slide only if the swipe distance is greater/equal than the trigger distance
      if (initialPos - lastPos >= triggerSwipeDistance) {
        this.$parent.slide('next');
      } else if (initialPos - lastPos < -triggerSwipeDistance) {
        this.$parent.slide('prev');
      }
      this.rafPending = true;

      window.requestAnimationFrame(this.resetFrame);
      this.initialTouchPos = {
        x: 0,
        y: 0,
      };
      this.initialTouchPos = {
        x: 0,
        y: 0,
      };
    },
    handleGestureStart(e) {
      if (e.touches && e.touches.length > 1) {
        return;
      }
      document.addEventListener('mousemove', this.handleGestureMove, true);
      document.addEventListener('mouseup', this.handleGestureEnd, true);

      this.initialTouchPos = this.getGesturePointFromEvent(e);
      this.lastTouchPos = this.getGesturePointFromEvent(e);

      this.$refs.carouselItem.style.transition = 'initial';
    },
    handleGestureMove(e) {
      if (!this.initialTouchPos) {
        return;
      }

      this.lastTouchPos = this.getGesturePointFromEvent(e);

      const differenceInY = this.initialTouchPos.y - this.lastTouchPos.y;
      const isScroll = Math.abs(differenceInY) > 10;

      if (!isScroll) {
        e.preventDefault();

        this.isScrolling = true;

        if (this.rafPending) {
          return;
        }

        this.rafPending = true;

        this.$nextTick(() => {
          window.requestAnimationFrame(this.onAnimFrame);
        });
      }
    },
    handleGestureEnd(e) {
      this.isScrolling = false;

      if (e.cancelable) {
        e.preventDefault();
      }

      if (e.touches && e.touches.length > 0) {
        return;
      }

      if (isEqual(this.initialTouchPos, this.lastTouchPos)) {
        this.clickHandler(this.index);
      }

      document.removeEventListener('mousemove', this.handleGestureMove, true);
      document.removeEventListener('mouseup', this.handleGestureEnd, true);

      this.swipe(this.initialTouchPos.x, this.lastTouchPos.x, e);
    },
    getGesturePointFromEvent(e) {
      const point = {};

      if (e.targetTouches) {
        // Prefer Touch Events
        point.x = e.targetTouches[0].clientX;
        point.y = e.targetTouches[0].clientY;
      } else {
        // Either Mouse event or Pointer Event
        point.x = e.clientX;
        point.y = e.clientY;
      }

      return point;
    },
    onAnimFrame() {
      if (!this.rafPending || !this.$refs.carouselItem || this.isScrolling) {
        return;
      }

      const differenceInX = this.initialTouchPos.x - this.lastTouchPos.x;
      const newXTransform = `${this.currentXPosition - differenceInX}px`;

      this.$refs.carouselItem.style.transform = `translateX(${newXTransform})`;

      this.rafPending = false;
    },
    resetFrame() {
      if (!this.rafPending || !this.$refs.carouselItem) {
        return;
      }

      this.$refs.carouselItem.style.transform = 'none';

      this.rafPending = false;
    },
  },
};
</script>
